import { useAuthsvsPasskeys } from '@egw/api'
import { SvgPasskeyAdded } from '@egw/assets'
import { registerPasskey, useAuth } from '@egw/auth'
import { Button, GenericError, IconPasskey } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

const log = new Logger('Enrollment - Joint - CreatePasskey')

export const CreatePasskeyPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { isAuthenticated, refreshToken } = useAuth()

  // Unused - TODO: Remove or fix
  // useValidateRoute()

  const [creatingPasskey, setCreatingPasskey] = useState<boolean>(false)
  const [passkeyPersisted, setPasskeyPersisted] = useState<boolean>(false)
  const [apiError, setApiError] = useState<boolean>(false)
  // Attempt to fetch passkeys if user is authenticated
  // Retry is false to prevent infinite loading state

  // Passkey data from Node API
  const {
    data: authsvsPasskeyData,
    isLoading: isGettingAuthsvsPasskeys,
    error: authsvsPasskeyError,
  } = useAuthsvsPasskeys({
    enabled: isAuthenticated,
    retry: false,
    refetchOnMount: 'always',
  })

  // Log passkey fetching errors
  useEffect(() => {
    if (authsvsPasskeyError) {
      log.error('Error fetching passkeys', authsvsPasskeyError)
    }
  }, [authsvsPasskeyError])

  // Sync authsvsPasskeyData with passkeyPersisted state
  useEffect(() => {
    if (authsvsPasskeyData && authsvsPasskeyData.length > 0) {
      setPasskeyPersisted(true)
    }
  }, [authsvsPasskeyData])

  // Refresh token on mount as user should be authenticated
  // This will ensure that if the user will reach Overview page
  useEffect(() => {
    void refreshToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addPasskeyHandler = async (): Promise<void> => {
    setCreatingPasskey(true)
    setApiError(false) // Clear previous error state
    try {
      await registerPasskey().then(async (res) => {
        if (res.__typename === 'RegisterWebAuthnSuccess') {
          setPasskeyPersisted(true)
        }
      })
    } catch (error) {
      setApiError(true)
      log.error('Error creating passkey during enrollment', error)
    } finally {
      setCreatingPasskey(false) // Always clear loading state
    }
  }

  const applicationId = ''

  // Prevent going back in history
  useEffect(() => {
    // Push a new state to the history stack
    window.history.pushState(null, '', window.location.href)

    // Add event listener for popstate
    const handlePopState = (): void => {
      // Push another state to prevent going back
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])
  useEffect(() => {
    sendEvent('bankjoint_enroll_passkey_visited', { applicationId })
  }, [applicationId])

  // const { mutate: getPasskeyCredentialOptions } = useGetJointEnrollmentPasskeyOptions()

  // const { mutate: continueEnrollmentFlow } = useSubmitEnrollmentHYCSSecureAccountData() // TODO: Uncomment when fixed

  const onContinue = (): void => {
    try {
      sendEvent('user_enroll_passkey_created', { applicationId })
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    } catch (e) {
      log.error('Error continuing enrollment flow after passkey creation', e)
    }
  }

  return (
    <EnrollmentJointLayout title={t('joint.create_passkey.page_title')}>
      <>
        {/* Loading state while fetching passkeys */}
        {isGettingAuthsvsPasskeys ? (
          <div className="w-full">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={1} height="100px" className="mb-10" />
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          </div>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isGettingAuthsvsPasskeys && !passkeyPersisted ? (
          <>
            <h1 className="typo-primary-2xl-light text-text-header mb-md">{t('joint.create_passkey.title')}</h1>
            <span className="typo-primary-lg-semibold text-text-header mb-lg">
              {t('joint.create_passkey.description')}
            </span>

            <IconPasskey className="w-1/3 h-fit" />

            <div className="flex flex-col mt-lg">
              {/* Block 1 */}
              <span className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.create_passkey.what_are_passkeys')}
              </span>
              <span className="typo-primary-base text-text-meta mt-0 mb-xl">
                {t('joint.create_passkey.what_are_passkeys_description')}
              </span>

              {/* Block 2 */}
              <span className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.create_passkey.where_passkeys_saved')}
              </span>
              <span className="typo-primary-base text-text-meta mt-0 mb-xl">
                {t('joint.create_passkey.where_passkeys_saved_description')}
              </span>

              {/* Block 3 */}
              <span className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.create_passkey.block_3_title')}
              </span>
              <span className="typo-primary-base text-text-meta mt-0">
                {t('joint.create_passkey.block_3_description')}
              </span>
            </div>

            <div className="flex flex-grow md:hidden" />

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isLoading={creatingPasskey}
              testId="registerPasskey_button"
              onClick={() => {
                const uuid = uuidv4()
                sendEvent('user_passkeyAdd_click', {
                  eventSource: 'egWealth',
                  category: 'passkey-add',
                  subCategory: 'intent',
                  passkeyAttemptUniqueId: uuid,
                })
                void addPasskeyHandler()
              }}
            >
              {t('joint.create_passkey.create_passkey')}
            </Button>
          </>
        ) : null}

        {passkeyPersisted && !isGettingAuthsvsPasskeys ? (
          <>
            <h1 className="typo-primary-2xl-light text-text-header mb-md">
              {t('joint.create_passkey.passkey_created_title')}
            </h1>

            <SvgPasskeyAdded className="w-1/3 h-fit" strokeColor="rgb(var(--color-green-500))" />

            <div className="flex flex-col gap-md mt-lg">
              <span className="typo-primary-base-semibold text-text-header">
                {t('joint.create_passkey.better_sign_in')}
              </span>
              <span className="typo-primary-base text-text-meta">{t('joint.create_passkey.single_tap')}</span>
              <span className="typo-primary-base-semibold text-text-header">
                {t('joint.create_passkey.manage_passkey')}
              </span>
              <span className="typo-primary-base text-text-meta">{t('joint.create_passkey.crud_description')}</span>
            </div>
            <div className="flex flex-grow md:hidden" />

            <Button
              type="submit"
              className="w-full md:w-min mt-xl flex"
              isLoading={creatingPasskey}
              testId="primaryAction_button"
              onClick={() => {
                onContinue()
              }}
            >
              {t('joint.create_passkey.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when passkey creation fails */}
        {apiError && !passkeyPersisted ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
            <Button
              type="button"
              className="w-full md:w-min mt-md"
              variant="secondary"
              onClick={addPasskeyHandler}
              isLoading={creatingPasskey}
              testId="retry-passkey_button"
            >
              {t('common.try_again', 'Try Again')}
            </Button>
          </div>
        ) : null}
      </>
    </EnrollmentJointLayout>
  )
}
